import React, { useState } from 'react';
import { SEO } from '../../../utils/seo';
import GlobalNavbar from '../../../components/feature/GlobalNavbar';
import Footer from '../../../components/feature/Footer';

const CantAffordRepairsToSellHousePage = () => {
  const [heroFormData, setHeroFormData] = useState({ name: '', phone: '', email: '', address: '' });
  const [bottomFormData, setBottomFormData] = useState({ name: '', phone: '', email: '', address: '', message: '' });
  const [heroSubmitting, setHeroSubmitting] = useState(false);
  const [bottomSubmitting, setBottomSubmitting] = useState(false);
  const [heroSuccess, setHeroSuccess] = useState(false);
  const [bottomSuccess, setBottomSuccess] = useState(false);

  const handleHeroSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setHeroSubmitting(true);
    try {
      await fetch('https://readdy.ai/api/form/d679b75biueuoqm1r0tg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(heroFormData as any).toString(),
      });
      setHeroSuccess(true);
      setHeroFormData({ name: '', phone: '', email: '', address: '' });
    } catch (error) {
      console.error('Form submission error:', error);
    }
    setHeroSubmitting(false);
  };

  const handleBottomSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBottomSubmitting(true);
    try {
      await fetch('https://readdy.ai/api/form/d679b75biueuoqm1r0ug', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(bottomFormData as any).toString(),
      });
      setBottomSuccess(true);
      setBottomFormData({ name: '', phone: '', email: '', address: '', message: '' });
    } catch (error) {
      console.error('Form submission error:', error);
    }
    setBottomSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Can't Afford Repairs to Sell Your House? | El Paso TX Solutions | TREX RE LLC"
        description="Tried to sell your house but can't afford the repairs? Learn your options for selling a home as-is in El Paso TX. Get a fair cash offer with no repairs needed. We buy houses in any condition."
        keywords="sell house no repairs El Paso, can't afford home repairs, sell house as-is Texas, cash home buyers no repairs, sell fixer upper El Paso"
        canonical="/elpaso-tx/cant-afford-repairs-to-sell-house"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: 'I Tried to Sell My House But Can\'t Afford the Repairs - What Are My Options?',
          description: 'Comprehensive guide for El Paso homeowners who need to sell but cannot afford required repairs',
          author: {
            '@type': 'Organization',
            name: 'TREX RE LLC',
          },
          publisher: {
            '@type': 'Organization',
            name: 'TREX RE LLC',
          },
        }}
      />
      <GlobalNavbar />

      {/* Hero Section */}
      <section className="relative min-h-[700px] flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/8f60446d006cc9256f18e9e45e41d966.jpg"
            alt="Homeowner concerned about repair costs"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-block px-4 py-2 bg-teal-600 text-white text-sm font-semibold rounded-full mb-6">
              No Repairs Required
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              I Tried to Sell My House But <span className="text-teal-400">Can&apos;t Afford the Repairs</span>
            </h1>
            <p className="text-xl text-gray-200 mb-8 leading-relaxed">
              You&apos;re not alone. Thousands of El Paso homeowners face this exact situation every year. The good news? You have options that don&apos;t require spending a dime on repairs.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="tel:+1-915-206-5814"
                className="inline-flex items-center px-6 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line mr-2"></i>
                Call (915) 206-5814
              </a>
            </div>
          </div>

          <div className="bg-white rounded-xl p-8 shadow-2xl">
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Get Your Free Cash Offer</h3>
            <p className="text-gray-600 mb-6">No repairs. No cleaning. No hassle.</p>
            {heroSuccess ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-3xl text-teal-600"></i>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">Thank You!</h4>
                <p className="text-gray-600">We&apos;ll contact you within 24 hours with your cash offer.</p>
              </div>
            ) : (
              <form id="hero-form" data-readdy-form onSubmit={handleHeroSubmit} className="space-y-4">
                <input
                  type="text"
                  name="name"
                  placeholder="Your Name"
                  value={heroFormData.name}
                  onChange={(e) => setHeroFormData({ ...heroFormData, name: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  required
                />
                <input
                  type="tel"
                  name="phone"
                  placeholder="Phone Number"
                  value={heroFormData.phone}
                  onChange={(e) => setHeroFormData({ ...heroFormData, phone: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  required
                />
                <input
                  type="email"
                  name="email"
                  placeholder="Email Address"
                  value={heroFormData.email}
                  onChange={(e) => setHeroFormData({ ...heroFormData, email: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  required
                />
                <input
                  type="text"
                  name="address"
                  placeholder="Property Address"
                  value={heroFormData.address}
                  onChange={(e) => setHeroFormData({ ...heroFormData, address: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  required
                />
                <button
                  type="submit"
                  disabled={heroSubmitting}
                  className="w-full py-4 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  {heroSubmitting ? 'Submitting...' : 'Get My Cash Offer'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 leading-relaxed mb-8">
              You&apos;ve made the decision to sell your house. Maybe you&apos;ve already listed it with a real estate
              agent, or perhaps you&apos;ve been exploring your options. Then reality hits: the inspection report
              comes back with a laundry list of repairs, or you already know your home needs significant work before
              anyone will buy it. The problem? You simply don&apos;t have the money to make those repairs.
            </p>

            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              This is one of the most frustrating situations a homeowner can face. You need to sell, but you can&apos;t
              afford to get your house &quot;market ready.&quot; Traditional buyers want move-in ready homes, and banks
              won&apos;t approve mortgages for properties with major issues. It feels like you&apos;re stuck in an
              impossible situation.
            </p>

            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              But here&apos;s what most people don&apos;t realize: <strong>you don&apos;t have to make repairs to sell your
              house</strong>. There are legitimate, proven alternatives that allow you to sell your property exactly as it
              sits today—no repairs, no renovations, no cleaning, and no out‑of‑pocket expenses. In this comprehensive guide,
              we&apos;ll explore every option available to El Paso homeowners who find themselves in this exact predicament.
            </p>

            <div className="bg-teal-50 border-l-4 border-teal-600 p-6 rounded-r-lg my-8">
              <p className="text-teal-800 font-medium">
                <strong>Key Takeaway:</strong> Selling a house that needs repairs isn&apos;t just possible—it happens every
                single day in El Paso. The key is understanding your options and choosing the path that makes the most
                sense for your specific situation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Repairs Are Required Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Why Are Repairs &quot;Required&quot; in Traditional Sales?</h2>

          <div className="mb-8">
            <img
              src="https://readdy.ai/api/search-image?query=home%20inspector%20examining%20damaged%20roof%20and%20foundation%20issues%20with%20clipboard%20taking%20notes%20at%20southwestern%20style%20house%20in%20el%20paso%20texas%20showing%20structural%20problems%20water%20damage%20professional%20inspection%20photography&width=1200&height=600&seq=no-repair-inspect-002&orientation=landscape"
              alt="Home inspector examining property issues"
              className="w-full h-80 object-cover object-top rounded-xl shadow-lg"
            />
          </div>

          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              Before we dive into solutions, it&apos;s important to understand why repairs become such a sticking point in
              traditional home sales. When you list your house on the open market, you&apos;re primarily targeting buyers who
              will finance their purchase through a mortgage. This is where the complications begin.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mt-10 mb-4">Lender Requirements</h3>
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              Banks and mortgage companies aren&apos;t just lending money to buyers—they&apos;re investing in the property as
              collateral. If a borrower defaults, the lender needs to be able to sell that property to recover their
              investment. For this reason, lenders require properties to meet certain minimum standards before they&apos;ll
              approve a loan.
            </p>

            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              FHA loans, which are popular among first-time homebuyers, have particularly strict property condition
              requirements. The home must be safe, sound, and secure. VA loans for military families have similar standards.
              Even conventional loans require the property to meet basic habitability standards.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mt-10 mb-4">Buyer Expectations</h3>
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              Beyond lender requirements, today&apos;s buyers have high expectations. They&apos;ve been watching home renovation
              shows and browsing perfectly staged homes online. When they walk into a property that needs work, they often
              can&apos;t see past the repairs to envision the home&apos;s potential. Even if they could get financing, many
              buyers simply don&apos;t want to deal with repairs after closing.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mt-10 mb-4">The Inspection Contingency Trap</h3>
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              Most purchase contracts include an inspection contingency, giving buyers the right to have the property
              professionally inspected. When that inspection reveals significant issues, buyers typically respond in one of
              three ways: they ask you to make repairs, they request a price reduction to cover the repair costs, or they
              walk away from the deal entirely.
            </p>

            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              If you can&apos;t afford to make the repairs and you&apos;re not willing to reduce your price further, you&apos;re
              back to square one—except now you&apos;ve wasted weeks or months and potentially paid for inspections, appraisals,
              and other costs.
            </p>
          </div>
        </div>
      </section>

      {/* Common Repair Issues Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Common Repair Issues That Block Traditional Sales</h2>

          <div className="mb-8">
            <img
              src="https://readdy.ai/api/search-image?query=collage%20showing%20common%20home%20repair%20issues%20damaged%20roof%20cracked%20foundation%20faulty%20electrical%20panel%20leaking%20plumbing%20old%20hvac%20system%20pest%20damage%20in%20southwestern%20style%20home%20professional%20real%20estate%20photography%20clean%20simple%20background&width=1200&height=600&seq=no-repair-issues-003&orientation=landscape"
              alt="Common repair issues in homes"
              className="w-full h-80 object-cover object-top rounded-xl shadow-lg"
            />
          </div>

          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              Let&apos;s look at the most common repair issues that prevent traditional sales and their typical costs in the
              El Paso market. Understanding these numbers helps explain why so many homeowners find themselves unable to
              afford necessary repairs.
            </p>

            <div className="space-y-8">
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  <i className="ri-home-4-line text-teal-600 mr-2"></i>Roof Damage
                </h3>
                <p className="text-gray-600 mb-3">
                  El Paso&apos;s intense sun and occasional severe weather can take a toll on roofs. Missing shingles, leaks,
                  or an aging roof that&apos;s past its lifespan will almost always be flagged by inspectors and lenders.
                </p>
                <p className="text-gray-900 font-semibold">Typical Cost: $8,000 - $25,000+</p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  <i className="ri-building-line text-teal-600 mr-2"></i>Foundation Issues
                </h3>
                <p className="text-gray-600 mb-3">
                  Foundation problems are particularly common in El Paso due to our soil conditions and climate. Cracks,
                  settling, or structural concerns are deal-breakers for most lenders.
                </p>
                <p className="text-gray-900 font-semibold">Typical Cost: $4,000 - $15,000+</p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  <i className="ri-flashlight-line text-teal-600 mr-2"></i>Electrical Problems
                </h3>
                <p className="text-gray-600 mb-3">
                  Outdated wiring, insufficient electrical panels, or safety hazards must be addressed before most lenders
                  will approve a loan. This is especially common in older El Paso homes.
                </p>
                <p className="text-gray-900 font-semibold">Typical Cost: $2,000 - $10,000+</p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  <i className="ri-drop-line text-teal-600 mr-2"></i>Plumbing Issues
                </h3>
                <p className="text-gray-600 mb-3">
                  Leaking pipes, sewer line problems, or outdated plumbing systems can prevent a sale. Water damage from
                  plumbing issues often leads to additional repair requirements.
                </p>
                <p className="text-gray-900 font-semibold">Typical Cost: $1,500 - $8,000+</p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  <i className="ri-temp-cold-line text-teal-600 mr-2"></i>HVAC System
                </h3>
                <p className="text-gray-600 mb-3">
                  In El Paso&apos;s climate, a functioning air conditioning system isn&apos;t a luxury—it&apos;s a necessity.
                  A broken or inefficient HVAC system will need to be repaired or replaced.
                </p>
                <p className="text-gray-900 font-semibold">Typical Cost: $3,000 - $12,000+</p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  <i className="ri-bug-line text-teal-600 mr-2"></i>Pest Damage
                </h3>
                <p className="text-gray-600 mb-3">
                  Termite damage, wood rot, or other pest-related issues must be remediated and repaired before most sales
                  can close. This often requires both treatment and structural repairs.
                </p>
                <p className="text-gray-900 font-semibold">Typical Cost: $1,000 - $5,000+</p>
              </div>
            </div>

            <div className="bg-amber-50 border-l-4 border-amber-600 p-6 rounded-r-lg my-8">
              <p className="text-amber-800">
                <strong>Reality Check:</strong> If your home needs multiple repairs from this list, you could be looking at
                $20,000 to $50,000 or more in repair costs. For many homeowners, especially those already facing financial
                challenges, this is simply impossible.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Your Options Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Your Options When You Can&apos;t Afford Repairs</h2>

          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              Now for the good news: you have several legitimate options for selling your house without making repairs.
              Let&apos;s explore each one, along with the pros and cons of each approach.
            </p>

            <div className="space-y-8">
              <div className="bg-white rounded-xl p-8 shadow-lg border-2 border-teal-600">
                <div className="flex items-start mb-4">
                  <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                    <span className="text-white font-bold text-xl">1</span>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Sell to a Cash Home Buyer</h3>
                    <p className="text-teal-600 font-semibold">RECOMMENDED FOR MOST SITUATIONS</p>
                  </div>
                </div>

                <p className="text-lg text-gray-600 leading-relaxed mb-4">
                  Cash home buyers are investors or companies that purchase properties directly from homeowners using cash,
                  not bank financing. Because they&apos;re not relying on a mortgage, they can buy homes in any condition—no
                  repairs required.
                </p>

                <div className="grid md:grid-cols-2 gap-6 mt-6">
                  <div>
                    <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                      <i className="ri-check-line text-green-600 mr-2"></i>Pros:
                    </h4>
                    <ul className="space-y-2 text-gray-600">
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-1"></i>
                        <span>No repairs or cleaning needed</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-1"></i>
                        <span>Fast closing (typically 7-14 days)</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-1"></i>
                        <span>No real estate commissions</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-1"></i>
                        <span>No showings or open houses</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-1"></i>
                        <span>Certainty of closing</span>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                      <i className="ri-close-line text-red-600 mr-2"></i>Cons:
                    </h4>
                    <ul className="space-y-2 text-gray-600">
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-gray-400 mr-2 mt-1"></i>
                        <span>Offer will be below retail market value</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-gray-400 mr-2 mt-1"></i>
                        <span>Need to research to find reputable buyers</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-teal-50 rounded-lg">
                  <p className="text-teal-800">
                    <strong>Best For:</strong> Homeowners who need to sell quickly, can&apos;t afford repairs, want certainty,
                    or are dealing with <a href="/about" className="text-teal-600 hover:text-teal-700 underline cursor-pointer">property challenges in El Paso</a>.
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-lg">
                <div className="flex items-start mb-4">
                  <div className="w-12 h-12 bg-gray-600 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                    <span className="text-white font-bold text-xl">2</span>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">List As-Is with a Real Estate Agent</h3>
                  </div>
                </div>

                <p className="text-lg text-gray-600 leading-relaxed mb-4">
                  You can list your property &quot;as-is&quot; on the MLS, making it clear that you won&apos;t be making any
                  repairs. This approach targets investors and buyers looking for fixer-uppers.
                </p>

                <div className="grid md:grid-cols-2 gap-6 mt-6">
                  <div>
                    <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                      <i className="ri-check-line text-green-600 mr-2"></i>Pros:
                    </h4>
                    <ul className="space-y-2 text-gray-600">
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-1"></i>
                        <span>Maximum market exposure</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-1"></i>
                        <span>Might get multiple offers</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-1"></i>
                        <span>Agent handles marketing</span>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                      <i className="ri-close-line text-red-600 mr-2"></i>Cons:
                    </h4>
                    <ul className="space-y-2 text-gray-600">
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-gray-400 mr-2 mt-1"></i>
                        <span>6% commission fees</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-gray-400 mr-2 mt-1"></i>
                        <span>Longer time on market</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-gray-400 mr-2 mt-1"></i>
                        <span>Deals may fall through</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-gray-400 mr-2 mt-1"></i>
                        <span>Must keep property showable</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-gray-400 mr-2 mt-1"></i>
                        <span>No guarantee of sale</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <p className="text-gray-700">
                    <strong>Best For:</strong> Homeowners who aren&apos;t in a rush, have a property in a desirable location,
                    and can afford to wait for the right buyer.
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-lg">
                <div className="flex items-start mb-4">
                  <div className="w-12 h-12 bg-gray-600 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                    <span className="text-white font-bold text-xl">3</span>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Offer Seller Financing</h3>
                  </div>
                </div>

                <p className="text-lg text-gray-600 leading-relaxed mb-4">
                  With seller financing, you act as the bank, allowing the buyer to make payments to you over time. This
                  bypasses traditional lender requirements.
                </p>

                <div className="grid md:grid-cols-2 gap-6 mt-6">
                  <div>
                    <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                      <i className="ri-check-line text-green-600 mr-2"></i>Pros:
                    </h4>
                    <ul className="space-y-2 text-gray-600">
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-1"></i>
                        <span>Attracts buyers who can&apos;t get traditional financing</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-1"></i>
                        <span>Can charge interest</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-1"></i>
                        <span>Potentially higher sale price</span>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                      <i className="ri-close-line text-red-600 mr-2"></i>Cons:
                    </h4>
                    <ul className="space-y-2 text-gray-600">
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-gray-400 mr-2 mt-1"></i>
                        <span>Don&apos;t receive full payment upfront</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-gray-400 mr-2 mt-1"></i>
                        <span>Risk of buyer default</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-gray-400 mr-2 mt-1"></i>
                        <span>Complex legal requirements</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-gray-400 mr-2 mt-1"></i>
                        <span>Ongoing involvement required</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <p className="text-gray-700">
                    <strong>Best For:</strong> Homeowners who don&apos;t need immediate cash and are comfortable with the
                    risks and responsibilities of being a lender.
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-lg">
                <div className="flex items-start mb-4">
                  <div className="w-12 h-12 bg-gray-600 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                    <span className="text-white font-bold text-xl">4</span>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Sell at Auction</h3>
                  </div>
                </div>

                <p className="text-lg text-gray-600 leading-relaxed mb-4">
                  Real estate auctions can sell properties quickly, often to investors who are comfortable buying as-is.
                </p>

                <div className="grid md:grid-cols-2 gap-6 mt-6">
                  <div>
                    <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                      <i className="ri-check-line text-green-600 mr-2"></i>Pros:
                    </h4>
                    <ul className="space-y-2 text-gray-600">
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-1"></i>
                        <span>Fast sale process</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-1"></i>
                        <span>Creates competitive bidding</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-1"></i>
                        <span>Set sale date</span>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                      <i className="ri-close-line text-red-600 mr-2"></i>Cons:
                    </h4>
                    <ul className="space-y-2 text-gray-600">
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-gray-400 mr-2 mt-1"></i>
                        <span>High auction fees (10-15%)</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-gray-400 mr-2 mt-1"></i>
                        <span>No minimum price guarantee</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-gray-400 mr-2 mt-1"></i>
                        <span>Property might not sell</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-gray-400 mr-2 mt-1"></i>
                        <span>Upfront marketing costs</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <p className="text-gray-700">
                    <strong>Best For:</strong> Unique properties or situations where competitive bidding might drive up the
                    price despite the property&apos;s condition.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Cash Buyers Can Purchase Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Why Cash Buyers Can Purchase Homes That Need Repairs</h2>

          <div className="mb-8">
            <img
              src="https://readdy.ai/api/search-image?query=professional%20cash%20home%20buyer%20shaking%20hands%20with%20relieved%20happy%20homeowner%20in%20front%20of%20southwestern%20style%20house%20in%20el%20paso%20texas%20showing%20trust%20and%20satisfaction%20warm%20natural%20lighting%20real%20estate%20transaction%20photography&width=1200&height=600&seq=no-repair-handshake-004&orientation=landscape"
              alt="Cash buyer helping homeowner"
              className="w-full h-80 object-cover object-top rounded-xl shadow-lg"
            />
          </div>

          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              You might be wondering: if traditional buyers can&apos;t purchase homes that need repairs, how can cash buyers
              do it? The answer lies in how they operate and what they plan to do with the property.
            </p>

            <div className="space-y-6">
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">No Lender Restrictions</h3>
                <p className="text-gray-600">
                  Cash buyers aren&apos;t using bank financing, so they don&apos;t have to meet lender property condition
                  requirements. They can purchase homes that wouldn&apos;t qualify for FHA, VA, or conventional loans.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">They&apos;re Experienced with Repairs</h3>
                <p className="text-gray-600">
                  Professional cash buyers typically have their own contractors and repair crews. They know exactly what
                  repairs will cost and how to complete them efficiently. What seems overwhelming to you is routine for them.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">They&apos;re Buying for Investment</h3>
                <p className="text-gray-600">
                  Cash buyers are investors. They plan to either fix and resell the property (flipping) or fix and rent it
                  out. They&apos;re not looking for a move-in ready home—they&apos;re looking for properties they can add
                  value to through repairs and improvements.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">They Buy in Bulk</h3>
                <p className="text-gray-600">
                  Many cash buyers purchase multiple properties per month. This volume allows them to negotiate better prices
                  on materials and labor, making repairs more cost-effective for them than for individual homeowners.
                </p>
              </div>
            </div>

            <div className="bg-teal-50 border-l-4 border-teal-600 p-6 rounded-r-lg my-8">
              <p className="text-teal-800">
                <strong>Important:</strong> Reputable cash buyers aren&apos;t trying to &quot;steal&quot; your house. They&apos;re
                providing a valuable service by purchasing properties that can&apos;t be sold through traditional channels,
                allowing homeowners to move forward with their lives without the burden of expensive repairs.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Math Behind Cash Offers Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">The Math Behind Cash Offers: Understanding the Real Cost</h2>

          <div className="mb-8">
            <img
              src="https://readdy.ai/api/search-image?query=diverse%20homeowners%20facing%20different%20repair%20challenges%20elderly%20couple%20young%20family%20single%20parent%20looking%20at%20damaged%20homes%20with%20concern%20in%20el%20paso%20texas%20showing%20various%20property%20situations%20professional%20real%20estate%20photography&width=1200&height=600&seq=no-repair-situations-005&orientation=landscape"
              alt="Homeowners facing repair challenges"
              className="w-full h-80 object-cover object-top rounded-xl shadow-lg"
            />
          </div>

          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              One of the biggest concerns homeowners have about selling to a cash buyer is the offer price. Yes, cash offers
              are typically below retail market value. But when you factor in all the costs and risks of a traditional sale,
              the difference often isn&apos;t as significant as it first appears.
            </p>

            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              Let&apos;s look at a real example using an El Paso home with an estimated retail value of $200,000 that needs
              $30,000 in repairs:
            </p>

            <div className="overflow-x-auto">
              <table className="w-full bg-white rounded-xl shadow-lg overflow-hidden">
                <thead className="bg-gray-900 text-white">
                  <tr>
                    <th className="px-6 py-4 text-left font-semibold">Item</th>
                    <th className="px-6 py-4 text-right font-semibold">Traditional Sale</th>
                    <th className="px-6 py-4 text-right font-semibold">Cash Sale</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-6 py-4 font-semibold text-gray-900">Sale Price</td>
                    <td className="px-6 py-4 text-right text-gray-900">$200,000</td>
                    <td className="px-6 py-4 text-right text-gray-900">$145,000</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-6 py-4 text-gray-600">Repairs Before Sale</td>
                    <td className="px-6 py-4 text-right text-red-600">-$30,000</td>
                    <td className="px-6 py-4 text-right text-gray-900">$0</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-gray-600">Real Estate Commission (6%)</td>
                    <td className="px-6 py-4 text-right text-red-600">-$12,000</td>
                    <td className="px-6 py-4 text-right text-gray-900">$0</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-6 py-4 text-gray-600">Closing Costs</td>
                    <td className="px-6 py-4 text-right text-red-600">-$3,000</td>
                    <td className="px-6 py-4 text-right text-gray-900">$0</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-gray-600">Holding Costs (6 months)</td>
                    <td className="px-6 py-4 text-right text-red-600">-$6,000</td>
                    <td className="px-6 py-4 text-right text-gray-900">$0</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-6 py-4 text-gray-600">Utilities During Listing</td>
                    <td className="px-6 py-4 text-right text-red-600">-$1,200</td>
                    <td className="px-6 py-4 text-right text-gray-900">$0</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-gray-600">Staging/Cleaning</td>
                    <td className="px-6 py-4 text-right text-red-600">-$1,500</td>
                    <td className="px-6 py-4 text-right text-gray-900">$0</td>
                  </tr>
                  <tr className="bg-teal-50">
                    <td className="px-6 py-4 font-bold text-gray-900">Net to Seller</td>
                    <td className="px-6 py-4 text-right font-bold text-gray-900">$146,300</td>
                    <td className="px-6 py-4 text-right font-bold text-teal-600">$145,000</td>
                  </tr>
                  <tr className="bg-gray-100">
                    <td className="px-6 py-4 font-bold text-gray-900">Time to Close</td>
                    <td className="px-6 py-4 text-right font-semibold text-gray-900">6-9 months</td>
                    <td className="px-6 py-4 text-right font-semibold text-teal-600">7-14 days</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="bg-teal-50 border-l-4 border-teal-600 p-6 rounded-r-lg my-8">
              <p className="text-teal-800 font-medium mb-3">
                <strong>The Bottom Line:</strong>
              </p>
              <p className="text-teal-800">
                In this example, the homeowner nets virtually the same amount with a cash sale ($145,000) as they would with
                a traditional sale ($146,300)—but they receive their money in 2 weeks instead of 6-9 months, avoid the stress
                of repairs and showings, and have certainty that the deal will close.
              </p>
            </div>

            <p className="text-lg text-gray-600 leading-relaxed mt-8">
              This example doesn&apos;t even account for the risk factors in a traditional sale: deals falling through after
              inspection, buyers backing out, additional price negotiations, or the property sitting on the market even
              longer. With a cash sale, you have certainty and speed.
            </p>
          </div>
        </div>
      </section>

      {/* Common Situations Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Common Situations Where Repairs Become a Barrier</h2>

          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              Let&apos;s look at some of the most common situations where El Paso homeowners find themselves unable to afford
              necessary repairs:
            </p>

            <div className="space-y-6">
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  <i className="ri-money-dollar-circle-line text-teal-600 mr-2"></i>Financial Hardship
                </h3>
                <p className="text-gray-600">
                  Job loss, medical bills, divorce, or other financial challenges have depleted your savings. You need to
                  sell to move forward, but you simply don&apos;t have the funds for repairs. This is one of the most common
                  reasons homeowners seek cash buyers.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  <i className="ri-home-heart-line text-teal-600 mr-2"></i>Inherited Property
                </h3>
                <p className="text-gray-600">
                  You&apos;ve inherited a house that needs work, but you don&apos;t have the money to invest in repairs for a
                  property you don&apos;t plan to keep. Many <a href="/rental-property" className="text-teal-600 hover:text-teal-700 underline cursor-pointer">rental properties that need extensive work</a> fall
                  into this category as well.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  <i className="ri-time-line text-teal-600 mr-2"></i>Deferred Maintenance
                </h3>
                <p className="text-gray-600">
                  Years of putting off repairs have accumulated into a long list of issues. The cost to address everything is
                  overwhelming, and you&apos;d rather sell quickly and let someone else handle it.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  <i className="ri-user-heart-line text-teal-600 mr-2"></i>Aging Homeowners
                </h3>
                <p className="text-gray-600">
                  You&apos;re ready to downsize or move to assisted living, but you don&apos;t have the energy, ability, or
                  desire to manage a major renovation project at this stage of life.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  <i className="ri-map-pin-line text-teal-600 mr-2"></i>Out-of-State Owners
                </h3>
                <p className="text-gray-600">
                  You own property in El Paso but live elsewhere. Managing repairs from a distance is impractical and
                  expensive. You&apos;d rather sell quickly and be done with it. This is especially common with <a href="/land-buyers" className="text-teal-600 hover:text-teal-700 underline cursor-pointer">vacant land or properties with significant structural issues</a>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How Selling to Cash Buyer Works Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">How Selling to a Cash Buyer Works: The 5-Step Process</h2>

          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              If you&apos;ve decided that selling to a cash buyer is the right option for your situation, here&apos;s what
              you can expect:
            </p>

            <div className="space-y-6">
              <div className="flex items-start">
                <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center mr-6 flex-shrink-0">
                  <span className="text-white font-bold text-xl">1</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Initial Contact</h3>
                  <p className="text-gray-600">
                    You reach out to a cash buyer (by phone, online form, or email) and provide basic information about your
                    property: address, condition, and your timeline for selling.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center mr-6 flex-shrink-0">
                  <span className="text-white font-bold text-xl">2</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Property Evaluation</h3>
                  <p className="text-gray-600">
                    The buyer schedules a brief walkthrough of your property. This isn&apos;t a formal inspection—they just
                    need to see the property&apos;s condition to make an accurate offer. The visit typically takes 15-30
                    minutes.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center mr-6 flex-shrink-0">
                  <span className="text-white font-bold text-xl">3</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Cash Offer Presentation</h3>
                  <p className="text-gray-600">
                    Within 24-48 hours, you receive a written cash offer. Reputable buyers will explain how they arrived at
                    the offer price and answer any questions you have. There&apos;S no obligation to accept.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center mr-6 flex-shrink-0">
                  <span className="text-white font-bold text-xl">4</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Accept and Sign</h3>
                  <p className="text-gray-600">
                    If you accept the offer, you&apos;ll sign a purchase agreement. You can choose your closing date—many
                    sellers close in as little as 7 days, though you can take longer if needed.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center mr-6 flex-shrink-0">
                  <span className="text-white font-bold text-xl">5</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Close and Get Paid</h3>
                  <p className="text-gray-600">
                    On the closing date, you&apos;ll meet at a title company to sign paperwork and receive your payment. The
                    entire process from initial contact to closing typically takes 7-14 days.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-teal-50 border-l-4 border-teal-600 p-6 rounded-r-lg my-8">
              <p className="text-teal-800">
                <strong>Remember:</strong> Throughout this process, you don&apos;t need to make any repairs, do any cleaning,
                or even remove your belongings if you don&apos;t want to. The buyer purchases the property exactly as it sits.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What to Look For Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">What to Look for in a Cash Home Buyer</h2>

          <div className="mb-8">
            <img
              src="https://readdy.ai/api/search-image?query=professional%20trustworthy%20cash%20home%20buying%20team%20in%20modern%20office%20with%20el%20paso%20texas%20real%20estate%20documents%20showing%20credibility%20and%20expertise%20warm%20professional%20lighting%20business%20photography&width=1200&height=600&seq=no-repair-team-006&orientation=landscape"
              alt="Professional cash buying team"
              className="w-full h-80 object-cover object-top rounded-xl shadow-lg"
            />
          </div>

          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              Not all cash buyers are created equal. Here&apos;s how to identify reputable companies and avoid potential
              problems:
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mb-6">Green Flags (Good Signs)</h3>

            <div className="space-y-4 mb-10">
              <div className="flex items-start bg-green-50 rounded-lg p-4">
                <i className="ri-check-line text-green-600 text-2xl mr-4 mt-1"></i>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Local Presence</h4>
                  <p className="text-gray-600">
                    They have a physical office or established presence in El Paso, not just a website or PO box.
                  </p>
                </div>
              </div>

              <div className="flex items-start bg-green-50 rounded-lg p-4">
                <i className="ri-check-line text-green-600 text-2xl mr-4 mt-1"></i>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Transparent Process</h4>
                  <p className="text-gray-600">
                    They clearly explain how they calculate offers and what the process involves.
                  </p>
                </div>
              </div>

              <div className="flex items-start bg-green-50 rounded-lg p-4">
                <i className="ri-check-line text-green-600 text-2xl mr-4 mt-1"></i>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">No Pressure Tactics</h4>
                  <p className="text-gray-600">
                    They give you time to consider the offer and don&apos;t pressure you to sign immediately.
                  </p>
                </div>
              </div>

              <div className="flex items-start bg-green-50 rounded-lg p-4">
                <i className="ri-check-line text-green-600 text-2xl mr-4 mt-1"></i>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Proof of Funds</h4>
                  <p className="text-gray-600">
                    They can provide proof they have the cash to close (bank statements, letters from lenders, etc.).
                  </p>
                </div>
              </div>

              <div className="flex items-start bg-green-50 rounded-lg p-4">
                <i className="ri-check-line text-green-600 text-2xl mr-4 mt-1"></i>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Professional Closing</h4>
                  <p className="text-gray-600">
                    They use a reputable title company for closing, not a &quot;friend&quot; or informal arrangement.
                  </p>
                </div>
              </div>

              <div className="flex items-start bg-green-50 rounded-lg p-4">
                <i className="ri-check-line text-green-600 text-2xl mr-4 mt-1"></i>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Positive Reviews</h4>
                  <p className="text-gray-600">
                    They have verifiable reviews and testimonials from previous sellers.
                  </p>
                </div>
              </div>

              <div className="flex items-start bg-green-50 rounded-lg p-4">
                <i className="ri-check-line text-green-600 text-2xl mr-4 mt-1"></i>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Clear Communication</h4>
                  <p className="text-gray-600">
                    They respond promptly to questions and keep you informed throughout the process.
                  </p>
                </div>
              </div>
            </div>

            <h3 className="text-2xl font-bold text-gray-900 mb-6">Red Flags (Warning Signs)</h3>

            <div className="space-y-4">
              <div className="flex items-start bg-red-50 rounded-lg p-4">
                <i className="ri-close-line text-red-600 text-2xl mr-4 mt-1"></i>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Upfront Fees</h4>
                  <p className="text-gray-600">
                    Legitimate cash buyers never charge fees before closing. They make their offer, and that&apos;s what you receive
                    (minus standard closing costs, which are typically covered by the buyer). If someone asks for upfront fees,
                    that&apos;s a red flag.
                  </p>
                </div>
              </div>

              <div className="flex items-start bg-red-50 rounded-lg p-4">
                <i className="ri-close-line text-red-600 text-2xl mr-4 mt-1"></i>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">High-Pressure Tactics</h4>
                  <p className="text-gray-600">
                    Beware of buyers who pressure you to sign immediately or claim the offer expires in hours.
                  </p>
                </div>
              </div>

              <div className="flex items-start bg-red-50 rounded-lg p-4">
                <i className="ri-close-line text-red-600 text-2xl mr-4 mt-1"></i>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Vague or Changing Offers</h4>
                  <p className="text-gray-600">
                    The offer should be clear and in writing. Be suspicious if the terms keep changing or aren&apos;t
                    documented.
                  </p>
                </div>
              </div>

              <div className="flex items-start bg-red-50 rounded-lg p-4">
                <i className="ri-close-line text-red-600 text-2xl mr-4 mt-1"></i>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">No Local Presence</h4>
                  <p className="text-gray-600">
                    Be cautious of out-of-state companies with no local office or track record in El Paso.
                  </p>
                </div>
              </div>

              <div className="flex items-start bg-red-50 rounded-lg p-4">
                <i className="ri-close-line text-red-600 text-2xl mr-4 mt-1"></i>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Unwilling to Provide References</h4>
                  <p className="text-gray-600">
                    Reputable buyers should be happy to provide references or proof of previous transactions.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Other Alternatives Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Other Alternatives to Consider</h2>

          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              Before making your final decision, here are a few other options worth considering:
            </p>

            <div className="space-y-6">
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Home Equity Loan or Line of Credit</h3>
                <p className="text-gray-600 mb-3">
                  If you have equity in your home, you might be able to borrow against it to fund repairs. However, this only
                  makes sense if you can afford the monthly payments and if the repairs will significantly increase your
                  home&apos;s value.
                </p>
                <p className="text-sm text-gray-500 italic">
                  Note: This option isn&apos;t available if you&apos;re already behind on payments or facing <a href="/avoid-foreclosure" className="text-teal-600 hover:text-teal-700 underline cursor-pointer">potential foreclosure</a>.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-3">FHA 203(k) Loan</h3>
                <p className="text-gray-600 mb-3">
                  This special FHA loan allows buyers to finance both the purchase price and repair costs in one mortgage.
                  However, you&apos;ll need to find a buyer willing to use this type of financing, and the process is more
                  complex than a traditional sale.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Negotiate Repairs with Buyer</h3>
                <p className="text-gray-600 mb-3">
                  In some cases, you might find a buyer willing to purchase the property and handle repairs themselves in
                  exchange for a reduced price. This is essentially what cash buyers do, but you might find an individual
                  buyer willing to do the same.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Rent Out the Property</h3>
                <p className="text-gray-600 mb-3">
                  If you can&apos;t afford repairs but don&apos;t need to sell immediately, you might consider renting the
                  property as-is. Some tenants are willing to rent homes that need work in exchange for lower rent. However,
                  you&apos;ll still need to ensure the property meets basic habitability standards.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Frequently Asked Questions</h2>

          <div className="space-y-6">
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Can I really sell my house without making any repairs?
              </h3>
              <p className="text-gray-600">
                Yes, absolutely. Cash buyers purchase homes in any condition. You don&apos;t need to fix anything, clean
                anything, or even remove your belongings. They buy the property exactly as it sits.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                How much less will I get from a cash buyer compared to listing traditionally?
              </h3>
              <p className="text-gray-600">
                Cash offers are typically 10-30% below retail market value, but when you factor in repair costs, commissions,
                closing costs, and holding costs, the net amount is often very similar. Plus, you get your money in weeks
                instead of months and avoid the risk of deals falling through.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                How quickly can I close with a cash buyer?
              </h3>
              <p className="text-gray-600">
                Most cash buyers can close in 7-14 days, though you can choose a longer timeline if you need more time to
                move. The timeline is flexible based on your needs.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Are there any fees when selling to a cash buyer?
              </h3>
              <p className="text-gray-600">
                Reputable cash buyers don&apos;t charge any fees. They make their offer, and that&apos;s what you receive
                (minus standard closing costs, which are typically covered by the buyer). If someone asks for upfront fees,
                that&apos;s a red flag.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                What if my house has serious problems like foundation issues or a bad roof?
              </h3>
              <p className="text-gray-600">
                Cash buyers specialize in purchasing homes with serious issues. Foundation problems, roof damage, fire damage,
                water damage—they&apos;ve seen it all and will still make an offer. In fact, these are exactly the types of
                properties they look for. If you have <a href="/sell-distressed-property" className="text-teal-600 hover:text-teal-700 underline cursor-pointer">distressed properties</a>, cash buyers are often your best option.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Can I sell if I&apos;m behind on mortgage payments?
              </h3>
              <p className="text-gray-600">
                Yes. Many cash buyers work with homeowners who are behind on payments or facing foreclosure. They can often
                close quickly enough to help you avoid foreclosure and protect your credit.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                What if I have tenants in the property?
              </h3>
              <p className="text-gray-600">
                Cash buyers can purchase occupied properties. Depending on the situation, they might ask the tenants to stay
                or work with you to transition them out. This is common with <a href="/rental-property" className="text-teal-600 hover:text-teal-700 underline cursor-pointer">rental properties</a>.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Do I need to disclose all the problems with my house?
              </h3>
              <p className="text-gray-600">
                Yes, you should always be honest about known issues. However, cash buyers expect problems—that&apos;s why
                they&apos;re buying as-is. Being upfront about issues helps them make an accurate offer and prevents problems
                at closing. If you have <a href="/title-issues" className="text-teal-600 hover:text-teal-700 underline cursor-pointer">title complications</a>, disclose those as well.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-white mb-4">Ready to Sell Your House Without Making Repairs?</h2>
            <p className="text-xl text-gray-300 mb-8">
              Get your free, no‑obligation cash offer today. No repairs. No commissions. No hassle.
            </p>
          </div>

          <div className="bg-white rounded-xl p-8 shadow-2xl max-w-2xl mx-auto">
            {bottomSuccess ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-3xl text-teal-600"></i>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">Thank You!</h4>
                <p className="text-gray-600">We&apos;ll be in touch within 24 hours with your cash offer.</p>
              </div>
            ) : (
              <form id="bottom-form" data-readdy-form onSubmit={handleBottomSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    value={bottomFormData.name}
                    onChange={(e) => setBottomFormData({ ...bottomFormData, name: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    required
                  />
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Phone Number"
                    value={bottomFormData.phone}
                    onChange={(e) => setBottomFormData({ ...bottomFormData, phone: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    required
                  />
                </div>
                <input
                  type="email"
                  name="email"
                  placeholder="Email Address"
                  value={bottomFormData.email}
                  onChange={(e) => setBottomFormData({ ...bottomFormData, email: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  required
                />
                <input
                  type="text"
                  name="address"
                  placeholder="Property Address"
                  value={bottomFormData.address}
                  onChange={(e) => setBottomFormData({ ...bottomFormData, address: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  required
                />
                <textarea
                  name="message"
                  placeholder="Any additional details about your property or situation..."
                  value={bottomFormData.message}
                  onChange={(e) =>
                    setBottomFormData({ ...bottomFormData, message: e.target.value.slice(0, 500) })
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm h-24 resize-none"
                  maxLength={500}
                />
                <button
                  type="submit"
                  disabled={bottomSubmitting}
                  className="w-full py-4 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  {bottomSubmitting ? 'Submitting...' : 'Get My Free Cash Offer Now'}
                </button>
              </form>
            )}

            <div className="mt-6 flex flex-wrap justify-center gap-6 text-sm text-gray-500">
              <span className="flex items-center">
                <i className="ri-check-line text-teal-600 mr-2"></i>No Repairs Needed
              </span>
              <span className="flex items-center">
                <i className="ri-check-line text-teal-600 mr-2"></i>No Commissions
              </span>
              <span className="flex items-center">
                <i className="ri-check-line text-teal-600 mr-2"></i>Close in 7-14 Days
              </span>
            </div>
          </div>

          <div className="text-center mt-8">
            <p className="text-gray-400 mb-4">Or call us directly:</p>
            <a
              href="tel:+1-915-206-5814"
              className="inline-flex items-center text-2xl font-bold text-teal-400 hover:text-teal-300 transition-colors cursor-pointer"
            >
              <i className="ri-phone-line mr-3"></i>(915) 206-5814
            </a>
          </div>
        </div>
      </section>

      {/* Related Resources */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Related Resources</h2>

          <div className="grid md:grid-cols-2 gap-6">
            <a href="/elpaso-tx" className="block p-6 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer">
              <h4 className="text-lg font-bold text-gray-900 mb-2">El Paso Real Estate Resource Center</h4>
              <p className="text-gray-600 text-sm">Explore more guides and resources for El Paso property owners.</p>
            </a>
            <a href="/sell-distressed-property" className="block p-6 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer">
              <h4 className="text-lg font-bold text-gray-900 mb-2">Sell Distressed Property</h4>
              <p className="text-gray-600 text-sm">Learn more about selling properties in any condition.</p>
            </a>
            <a href="/inherited-homes" className="block p-6 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer">
              <h4 className="text-lg font-bold text-gray-900 mb-2">Inherited Homes</h4>
              <p className="text-gray-600 text-sm">Special considerations for selling inherited properties.</p>
            </a>
            <a href="/get-cash-offer-for-my-property" className="block p-6 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer">
              <h4 className="text-lg font-bold text-gray-900 mb-2">Get Your Cash Offer</h4>
              <p className="text-gray-600 text-sm">Request your free, no‑obligation cash offer today.</p>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CantAffordRepairsToSellHousePage;
